import {
  ArrowLeftOutlined,
  LoadingOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Select, theme } from "antd";
import { DashboardV1State, DeepLinkType } from "pages/api/deep-links/types";
import { useState } from "react";
import { BuildingIcon } from "src/components/BuildingIcon";
import { Flex } from "src/components/Flex";
import { VzblIcon } from "src/components/VzblIcon";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { isNewDashboardId, isStartScreenId, START_STATE_ID } from "./context";
import { useApplicationContext } from "src/hooks/useApplication";

export function DashboardSelect(props: {
  bordered?: boolean;
  disabled?: boolean;
  filter?: ({ value }: { value: string }) => boolean;
  onChange(id: string): void;
  searchTerm: string;
  setSearchTerm(text: string): void;
  showResults?: boolean;
  unlock(): void;
  value?: string;
}) {
  const { token } = theme.useToken();
  const links = useDeepLinks(DeepLinkType.BOARD);
  const app = useApplicationContext();
  const [open, setOpen] = useState(false);
  const onStart = isStartScreenId(props.value);

  // Only filter out system boards for non-superadmins
  const filterSystemBoards = (link: any) => {
    if (app.isSuperAdmin) return true;
    return !(link.json as DashboardV1State | undefined)?.systemName;
  };

  return (
    <Select
      bordered={props.bordered === undefined ? false : props.bordered}
      disabled={props.disabled}
      onClear={() => props.setSearchTerm("")}
      allowClear={Boolean(
        open &&
          props.searchTerm &&
          props.value &&
          props.value !== START_STATE_ID
      )}
      style={{ width: "100%" }}
      suffixIcon={onStart ? null : undefined}
      showSearch
      popupMatchSelectWidth={false}
      optionFilterProp="textLabel"
      optionLabelProp="selectedLabel"
      filterOption={(input, option) =>
        (option?.textLabel ?? "")
          .toLowerCase()
          .includes(input.trim().toLowerCase())
      }
      notFoundContent={links.links.isFetched ? null : <LoadingOutlined />}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onDropdownVisibleChange={(open_) => setOpen(open_)}
      onSearch={(input) => (open || input) && props.setSearchTerm(input)}
      searchValue={props.searchTerm}
      filterSort={(a, b) => {
        if (isStartScreenId(a.value)) return -1;
        if (isStartScreenId(b.value)) return 1;
        if (isNewDashboardId(a.value)) return -1;
        if (isNewDashboardId(b.value)) return 1;
        return a.textLabel
          .toLowerCase()
          .localeCompare(b.textLabel.toLowerCase());
      }}
      open={onStart && !props.showResults ? false : undefined}
      options={[
        {
          key: START_STATE_ID,
          value: START_STATE_ID,
          label: (
            <span
              style={{
                fontWeight: 600,
                display: "inline-block",
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <ArrowLeftOutlined /> Back to all boards
            </span>
          ),
          textLabel: "All boards",
          selectedLabel: (
            <span style={{ opacity: 0.5 }}>
              {props.searchTerm || "find board..."}
            </span>
          ),
        },
        ...(links.links.data || []).filter(filterSystemBoards).map((link) => ({
          key: "key" + link.id,
          label: (
            <Flex gap={8} centeredY start>
              <span style={{ color: token.colorTextQuaternary }}>
                {link.userId ? (
                  <LockOutlined />
                ) : link.orgId ? (
                  <BuildingIcon />
                ) : (
                  <VzblIcon style={{ opacity: 0.5 }} />
                )}
              </span>
              <span>{link.name}</span>
            </Flex>
          ),
          textLabel: link.name,
          value: link.id,
          selectedLabel: <span style={{ opacity: 0.5 }}>find board...</span>,
        })),
      ].filter(props.filter || (() => true))}
      loading={links.links.isLoading}
      placeholder={"find board..."}
      value={props.value || START_STATE_ID}
      onChange={(id) => {
        if (Array.isArray(id)) return; // unexpected.
        props.onChange(id);
        if (isNewDashboardId(id)) props.unlock();
      }}
    />
  );
}
